/**
 * useRenderLoopGuard
 *
 * Logs a warning if a component renders more than `threshold` times within `windowMs`.
 * Helps detect infinite re-render loops (React error #185) and identify the culprit component.
 */
import { useEffect, useRef } from 'react';

export function useRenderLoopGuard(
  name: string,
  threshold: number = 30,
  windowMs: number = 300
) {
  const countRef = useRef(0);
  const startRef = useRef<number | null>(null);
  const warnedRef = useRef(false);

  // Count each render
  countRef.current += 1;
  const now = Date.now();
  if (startRef.current == null) startRef.current = now;

  const elapsed = now - startRef.current;
  if (elapsed <= windowMs) {
    if (!warnedRef.current && countRef.current >= threshold) {
      warnedRef.current = true;
      // Capture a lightweight stack sample
      const stack = new Error().stack?.split('\n').slice(0, 8).join('\n');
      console.warn(
        `[RenderLoopGuard] ${name}: ${countRef.current} renders in ${elapsed}ms (threshold ${threshold}/${windowMs}ms).`,
        { name, count: countRef.current, elapsedMs: elapsed, threshold, windowMs, stack }
      );
    }
  } else {
    // Reset window
    startRef.current = now;
    countRef.current = 1;
    warnedRef.current = false;
  }

  // Reset on unmount
  useEffect(() => {
    return () => {
      countRef.current = 0;
      startRef.current = null;
      warnedRef.current = false;
    };
  }, []);
}
